import { compareDesc } from 'date-fns'

import { SortType } from 'appTypes/shared'
import { Location } from '@leid/api-locations'

export const sortItems = (sortType: SortType, data: any): any => {
  if (sortType.name === 'rentedFrom' || sortType.name === 'rentedTo') {
    return sortItemsByDates(sortType, data)
  }

  return sortByTextOrNumber(sortType, data)
}

const sortItemsByDates = (sortType: SortType, data: any) => {
  return [...data].sort((a: any, b: any) => {
    const aParam = new Date(a[sortType.name])
    const bParam = new Date(b[sortType.name])

    return sortType.type === 'asc' ? compareDesc(aParam, bParam) : compareDesc(bParam, aParam)
  })
}

const sortByTextOrNumber = (sortType: SortType, data: any) => {
  return [...data].sort((a: any, b: any) => {
    const aParam = a[sortType.name]
    const bParam = b[sortType.name]

    const firstParam = typeof aParam === 'number' ? aParam : aParam && aParam.toLowerCase()
    const secondParam = typeof bParam === 'number' ? bParam : bParam && bParam.toLowerCase()

    const sortVal = sortType.type === 'asc' ? firstParam < secondParam : firstParam > secondParam

    if (sortVal) {
      return -1
    }

    return 1
  })
}

export type LocationWithDistance = Location & {
  distance: number
}

type Coordinates = {
  lat: number | undefined
  long: number | undefined
}

export const sortLocationsByDistance = (mainPoint: Coordinates, locations: Location[]): LocationWithDistance[] => {
  if (mainPoint.lat === undefined || mainPoint.long === undefined) {
    return locations.map((l) => ({ ...l, distance: 0 }))
  }

  const locationsWithDistance: LocationWithDistance[] = locations
    .map((l) => ({
      ...l,
      distance: calcDistance(mainPoint.lat ?? 0, mainPoint.long ?? 0, l.lat, l.long),
    }))
    .sort((a, b) => a.distance - b.distance)

  return locationsWithDistance
}

export const sortByDistance = <T extends { lat: number; long: number }>(mainPoint: Coordinates, list: T[]): T[] => {
  if (mainPoint.lat === undefined || mainPoint.long === undefined) {
    return list
  }

  const withDistance = list
    .map((l) => ({
      ...l,
      distance: calcDistance(mainPoint.lat ?? 0, mainPoint.long ?? 0, l.lat, l.long),
    }))
    .sort((a, b) => a.distance - b.distance)

  return withDistance
}

// Converts numeric degrees to radians
// eslint-disable-next-line class-methods-use-this
const toRad = (Value: number) => {
  return (Value * Math.PI) / 180
}

const calcDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371 // km
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const lat1Rad = toRad(lat1)
  const lat2Rad = toRad(lat2)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1Rad) * Math.cos(lat2Rad)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const d = R * c
  return d
}
